import { FC, ReactNode, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { LinkButton, Modal } from 'src/components';
import { ROUTES } from 'src/constants/routes.const';
import { useUserContext } from 'src/context/user';
import { markPronunciationUpsellShown } from './pronunciationUpsellStorage';
import { LocalPronunciationClip } from './useLocalPronunciationClip';
import { getModalDismissMethod, usePronunciationUpsell } from './usePronunciationUpsell';
import * as S from './styles';

const msgs = defineMessages({
  title: {
    id: 'pronunciation_upsell_title',
    defaultMessage: 'Repeating the locals in the videos?',
  },
  description: {
    id: 'pronunciation_upsell_plan_description',
    defaultMessage: 'Unlimited pronunciation practice with locals.',
  },
  /* Shared with the plans page so the annual term reads the same and stays translated once. */
  planPeriod: {
    id: 'new_plans_card_12_months',
    defaultMessage: '12 months',
  },
  socialProof: {
    id: 'pronunciation_upsell_social_proof',
    defaultMessage: 'Trusted by <strong>370,000+ PRO learners</strong>',
  },
  /* Quote marks differ by language, so they live in the message rather than the markup. */
  clipCaption: {
    id: 'pronunciation_upsell_clip_caption',
    defaultMessage: '“{word}”',
  },
  cta: {
    id: 'pronunciation_upsell_cta',
    defaultMessage: 'Improve your pronunciation',
  },
  skip: {
    id: 'pronunciation_upsell_skip',
    defaultMessage: 'Stay on free',
  },
});

const SOURCE = 'pronunciation_first_session';

const AVATARS = [
  { letter: 'M', color: 'secondary20' },
  { letter: 'A', color: 'warning20' },
  { letter: 'L', color: 'tertiary30' },
  { letter: 'J', color: 'secondary40' },
] as const;

export type PronunciationUpsellProps = {
  onContinue: () => void;
  /** Overrides the Learn with Locals clip, primarily for previews and tests. */
  clip?: LocalPronunciationClip;
};

/** Sells pronunciation practice to a Path learner after their first Learn session. */
export const PronunciationUpsell: FC<PronunciationUpsellProps> = ({
  onContinue,
  clip: clipOverride,
}) => {
  const intl = useIntl();
  const { user } = useUserContext();
  const { clip, formattedYearlyPrice, handleSubscribeClick, handleDismiss } =
    usePronunciationUpsell({ source: SOURCE, onContinue, clipOverride });

  useEffect(() => {
    markPronunciationUpsellShown(user);
    // Once per learner, so only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      isOpen
      fullSize
      hideCloseButton
      handleClose={event => handleDismiss(getModalDismissMethod(event))}
    >
      <S.ContentOuterContainer data-testid="pronunciation-upsell">
        <S.ContentInnerContainer>
          <S.Columns $hasVideo={!!clip}>
            {clip && (
              <S.Stage>
                <S.Clip src={clip.src} playOnKeyboardShortcut={false} autoPlay />
                <S.Phrase dir="auto">
                  {intl.formatMessage(msgs.clipCaption, { word: clip.phrase })}
                </S.Phrase>
              </S.Stage>
            )}
            <S.Card>
              <S.Title>{intl.formatMessage(msgs.title)}</S.Title>
              <S.Divider />
              <S.PlanSection>
                <S.PlanRow>
                  <span>{intl.formatMessage(msgs.planPeriod)}</span>
                  {formattedYearlyPrice && (
                    <span data-testid="pronunciation-upsell-price">{formattedYearlyPrice}</span>
                  )}
                </S.PlanRow>
                <S.PlanDescription>{intl.formatMessage(msgs.description)}</S.PlanDescription>
              </S.PlanSection>
              <S.Divider />
              <S.SocialProof>
                <S.Avatars aria-hidden>
                  {AVATARS.map(({ letter, color }) => (
                    <S.Avatar key={letter} $color={color}>
                      {letter}
                    </S.Avatar>
                  ))}
                </S.Avatars>
                <span>
                  {intl.formatMessage(msgs.socialProof, {
                    strong: (...chunks: ReactNode[]) => <strong key="strong">{chunks}</strong>,
                  })}
                </span>
              </S.SocialProof>
            </S.Card>
          </S.Columns>
          <S.ButtonContainer>
            <LinkButton
              buttonType="primary"
              buttonSize="large"
              wide
              href={{ pathname: ROUTES.payment.plans, query: { source: SOURCE } }}
              onClick={handleSubscribeClick}
            >
              {intl.formatMessage(msgs.cta)}
            </LinkButton>
            <S.FreeLink
              type="button"
              onClick={() => handleDismiss('stay_on_free')}
              data-testid="pronunciation-upsell-skip"
            >
              {intl.formatMessage(msgs.skip)}
            </S.FreeLink>
          </S.ButtonContainer>
        </S.ContentInnerContainer>
      </S.ContentOuterContainer>
    </Modal>
  );
};
